/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, usePathname, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { ProfilesSwitch, useIsKidsProfilesVisible } from "@packages/kids";
import type { Menu } from "@packages/sdk";
import {
  useIsBlockedFromPurchasing,
  useIsStudent,
  useIsTeacher,
  usePathMatch,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import {
  AccountIcon,
  Avatar,
  BlackboardIcon,
  Button,
  CloseIcon,
  constantSemanticColors,
  cssMediaQueryMinWidthDesktop,
  cssMediaQueryMinWidthTablet,
  DownloadAppButton,
  DropdownMenuItem,
  fontSizes,
  FullscreenModal,
  FullscreenModalDropdownMenu,
  FullscreenModalFooter,
  FullscreenModalItem,
  HallowLogoHarold,
  HallowLogoText,
  IconButton,
  LogoutButton,
  MenuIcon,
  Notice,
  numericPixels,
  SearchIcon,
  spacing,
  styleDynamics,
  useDynamicViewport,
  zIndices,
} from "@packages/ui";
import { DataTestId, HallowDataTestId } from "@tools/playwright/global";
import { useEffect, useMemo, useState } from "react";

import type { LINK } from "../../constants";
import { LINKS } from "../../constants";
import { WEB_APP_FULL_SCREEN_ROUTES } from "../../lib/constants/routes.constants";
import { MenuBarPage } from "../MenuBarPage";
import { AdminPageTitle } from "./AdminPageTitle";
import { MenuBarDashboardMobileChildren } from "./MenuBarDashboardMobileChildren";
import { TryHallowForFree } from "./TryHallowForFree";

const dropdownMenuItemStyles: Interpolation = {
  minWidth: numericPixels[240],
};

const justifyContentFlexEndStyles: Interpolation = {
  justifyContent: "flex-end",
};

const modalFullscreenFooterStyles: Interpolation = {
  gap: spacing.m,
};

const modalFullscreenFooterDivDynamicStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  flexDirection: "column",
  ["@media (min-width: 370px)"]: {
    flexDirection: "row",
  },
};

const modalFullscreenFooterDivVerticalStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  marginTop: spacing.xl,
};

// CSS variable rather than theme.semanticColors.* — Emotion's theme-dependent
// class can get stuck on the SSR'd light value through a hydration mismatch.
// --sc-* live on the ThemeProvider body and always reflect the current theme.
const navStyles: Interpolation = {
  alignItems: "center",
  backgroundColor: "var(--sc-background)",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  height: spacing.xxxl,
  position: "sticky",
  top: spacing.none,
  zIndex: zIndices.menuBar,
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    paddingBottom: 0,
    paddingTop: 0,
  },
};

const darkOverlayNavStyles: Interpolation = {
  "&&": {
    background: "transparent",
    backgroundColor: "transparent",
  },
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
};

const darkOverlayLogoStyles: Interpolation = {
  color: constantSemanticColors.onColorLowest,
  "&& path": {
    fill: constantSemanticColors.onColorLowest,
  },
};

// Same reason as navStyles, applied to transparentPrimary's color tokens.
// && doubles class specificity so this wins against Button's variant class.
const transparentPrimaryThemeOverride: Interpolation = {
  "&&": {
    color: "var(--sc-primary)",
    "&:focus-visible": {
      backgroundColor: "var(--sc-neutrals-lowest)",
      color: "var(--sc-neutrals-medium)",
    },
    "@media (hover: hover)": {
      "&:hover": {
        backgroundColor: "var(--sc-neutrals-lowest)",
        color: "var(--sc-primary)",
      },
    },
    "&:active": {
      backgroundColor: "var(--sc-neutrals-lowest)",
      color: "var(--sc-neutrals-medium)",
    },
  },
};

const darkOverlayDesktopTransparentPrimaryStyles: Interpolation = {
  [cssMediaQueryMinWidthDesktop]: {
    "&&": {
      color: constantSemanticColors.onColorLowest,
      "&:focus-visible": {
        backgroundColor: "transparent",
        color: constantSemanticColors.onColorLowest,
      },
      "@media (hover: hover)": {
        "&:hover": {
          backgroundColor: "transparent",
          color: constantSemanticColors.onColorLowest,
        },
      },
      "&:active": {
        backgroundColor: "transparent",
        color: constantSemanticColors.onColorLowest,
      },
    },
  },
};

const darkOverlayDesktopCtaStyles: Interpolation = {
  [cssMediaQueryMinWidthDesktop]: {
    "&&": {
      backgroundColor: constantSemanticColors.onColorLowest,
      color: constantSemanticColors.onColorHighest,
      "&:focus-visible": {
        backgroundColor: constantSemanticColors.onColorLowest,
        color: constantSemanticColors.onColorHighest,
      },
      "@media (hover: hover)": {
        "&:hover": {
          backgroundColor: constantSemanticColors.onColorLowest,
          color: constantSemanticColors.onColorHighest,
        },
      },
      "&:active": {
        backgroundColor: constantSemanticColors.onColorLowest,
        color: constantSemanticColors.onColorHighest,
      },
    },
  },
};

const authenticatedNavStyles: Interpolation = {
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.m,
    paddingRight: spacing.m,
  },
};

const unauthenticatedNavStyles: Interpolation = {
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.xxl,
    paddingRight: spacing.xxl,
  },
};

const ulStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const ulVerticalStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};

const ulVerticalFlexGrow1Styles: Interpolation = {
  flexGrow: 1,
};

const widthFitContentStyles: Interpolation = {
  width: "fit-content",
};

const haroldLogoStyles: Interpolation = {
  marginLeft: "10px",
  marginRight: "10px",
  marginTop: spacing.s,
  marginBottom: spacing.s,
};

const badgeStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  fontSize: fontSizes.bodyS,
  fontWeight: 600,
  gap: spacing.xs,
  margin: spacing.none,
  padding: spacing.xxs,
  paddingRight: spacing.s,
};

const menuBarLinkContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.t,
};

const MENU_BAR_HIDDEN_ROUTES = [
  "/upgrade/subscribe",
  "/web/subscribe",
  "/upgrade/checkout",
  "/support-the-mission",
  ...WEB_APP_FULL_SCREEN_ROUTES,
];

export type MenuBarProps = {
  menu?: Menu;
};

export const MenuBar = ({ menu = [] }: MenuBarProps) => {
  const isDesktopViewport = useDynamicViewport({ minimumWidth: 1024 });
  const isDesktopDashboardViewport = useDynamicViewport({ minimumWidth: 768 });
  const matcher = usePathMatch();
  const isMenuBarHidden = MENU_BAR_HIDDEN_ROUTES.some((route) =>
    matcher(route),
  );
  const pathname = usePathname();
  const isInAdmin = pathname.includes("/admin");
  const { status } = useSession();
  const { query: meQuery } = useRequestMe();
  const t = useTranslations();
  const isTeacher = useIsTeacher();
  const isStudent = useIsStudent();
  const analytics = useAnalytics();
  const isDarkOverlayVariant = pathname === "/" && status != "authenticated";

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [view, setView] = useState<"authenticated" | "unauthenticated">(
    "unauthenticated",
  );

  const isKidsProfileVisible = useIsKidsProfilesVisible();
  const isBlockedFromPurchasing = useIsBlockedFromPurchasing();
  const profileSwitch = useMemo(
    () =>
      isKidsProfileVisible && (
        <ProfilesSwitch
          onClick={() => setIsMenuOpen(false)}
          switchProfilesProps={{
            onSuccess: () => window.location.assign("/home"),
          }}
        />
      ),
    [isKidsProfileVisible],
  );

  useEffect(() => {
    setView(status === "authenticated" ? "authenticated" : "unauthenticated");
  }, [status]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  const trigger = (
    <IconButton
      css={[
        !isDarkOverlayVariant && transparentPrimaryThemeOverride,
        isDarkOverlayVariant && darkOverlayDesktopTransparentPrimaryStyles,
      ]}
      icon={isMenuOpen ? <CloseIcon /> : <MenuIcon />}
      variant={isDarkOverlayVariant ? "secondary" : "transparentPrimary"}
    />
  );

  if (isMenuBarHidden) return null;

  return (
    <nav
      css={[
        navStyles,
        view === "authenticated"
          ? authenticatedNavStyles
          : unauthenticatedNavStyles,
        isDarkOverlayVariant && darkOverlayNavStyles,
      ]}
      data-testid={HallowDataTestId.MenuBar}
    >
      <div css={menuBarLinkContainerStyles}>
        <Link
          href={status === "authenticated" ? "/home" : "/"}
          prefetch={false}
          css={widthFitContentStyles}
        >
          {view !== "authenticated" && (
            <HallowLogoText
              data-testid={DataTestId.menuBarUnauthenticatedLink}
              css={isDarkOverlayVariant ? darkOverlayLogoStyles : undefined}
              fill={
                isDarkOverlayVariant
                  ? constantSemanticColors.onColorLowest
                  : undefined
              }
            />
          )}
          {view === "authenticated" && (
            <HallowLogoHarold
              data-testid={DataTestId.menuBarAuthenticatedLink}
              css={haroldLogoStyles}
            />
          )}
        </Link>
        {isInAdmin &&
          !isDesktopDashboardViewport &&
          view === "authenticated" && <AdminPageTitle />}
      </div>

      <ul css={ulStyles}>
        {view === "authenticated" && (
          <>
            {[LINKS.home, LINKS.search, LINKS.bible].map((link: LINK) => (
              <li
                key={link.message}
                css={styleDynamics.displayTabletViewport()}
              >
                <MenuBarPage
                  icon={link.icon}
                  analyticsEvent={link.analyticsEvent}
                  {...("menuProps" in link ? link.menuProps : {})}
                  {...link.props}
                >
                  {t(link.message)}
                </MenuBarPage>
              </li>
            ))}
            <li css={styleDynamics.displayTabletViewport()}>
              <MenuBarPage
                override={
                  <Avatar
                    selected={matcher(LINKS.me.props.href.toString())}
                    imgSrc={meQuery.data?.image_url}
                  />
                }
                analyticsEvent={LINKS.me.analyticsEvent}
                {...LINKS.me.props}
              >
                {t(LINKS.me.message)}
              </MenuBarPage>
            </li>
            {meQuery.data?.has_access_to_admin_dashboard && (
              <li css={styleDynamics.displayTabletViewport()}>
                <MenuBarPage
                  analyticsEvent={LINKS.parishAdminDashboard.analyticsEvent}
                  data-testid={
                    DataTestId.menuBarAdminCommunityParishAdminDashboardLink
                  }
                  icon={LINKS.parishAdminDashboard.icon}
                  {...LINKS.parishAdminDashboard.props}
                >
                  {t(LINKS.parishAdminDashboard.message)}
                </MenuBarPage>
              </li>
            )}
          </>
        )}

        {view !== "authenticated" &&
          menu.map((link) => (
            <li key={link.id} css={styleDynamics.displayDesktopViewport()}>
              <Button
                asChild
                css={[
                  transparentPrimaryThemeOverride,
                  isDarkOverlayVariant &&
                    darkOverlayDesktopTransparentPrimaryStyles,
                ]}
                variant="transparentPrimary"
              >
                <a href={link.url} target={link.target}>
                  {link.title}
                </a>
              </Button>
            </li>
          ))}
      </ul>

      <ul css={[ulStyles, justifyContentFlexEndStyles]}>
        {view === "authenticated" && (
          <>
            {isStudent && (
              <Notice
                icon={<AccountIcon />}
                css={[styleDynamics.displayDesktopViewport(), badgeStyles]}
              >
                {t("web_menu_school_account")}
              </Notice>
            )}
            {isTeacher && (
              <a href={"https://teachers.hallow.com"} target={"_blank"}>
                <Notice
                  icon={<BlackboardIcon />}
                  css={[
                    styleDynamics.displayDesktopViewport("flex"),
                    badgeStyles,
                  ]}
                >
                  {t("web_teacher_resources_link")}
                </Notice>
              </a>
            )}
            <FullscreenModalDropdownMenu
              dropdownMenuProps={{
                align: "end",
                children: (
                  <>
                    {[
                      ...(!isBlockedFromPurchasing ? [LINKS.giftCards] : []),
                      LINKS.account,
                    ].map((link) => (
                      <DropdownMenuItem
                        asChild
                        key={link.message}
                        css={dropdownMenuItemStyles}
                      >
                        <Link {...link.props}>{t(link.message)}</Link>
                      </DropdownMenuItem>
                    ))}
                    {isKidsProfileVisible && (
                      <DropdownMenuItem
                        asChild
                        key={"kids_profile"}
                        css={dropdownMenuItemStyles}
                      >
                        {profileSwitch}
                      </DropdownMenuItem>
                    )}
                    {meQuery.isSuccess &&
                      meQuery.data?.sign_in_methods?.length > 0 && (
                        <DropdownMenuItem
                          asChild
                          key={"login_title"}
                          css={dropdownMenuItemStyles}
                        >
                          <LogoutButton />
                        </DropdownMenuItem>
                      )}
                  </>
                ),
              }}
              fullscreenModalProps={{
                children: isInAdmin ? (
                  <ul css={[ulVerticalStyles, ulVerticalFlexGrow1Styles]}>
                    <MenuBarDashboardMobileChildren />
                  </ul>
                ) : (
                  <ul css={ulVerticalStyles}>
                    {[
                      LINKS.search,
                      ...(!isBlockedFromPurchasing ? [LINKS.giftCards] : []),
                      LINKS.account,
                    ].map((link) => (
                      <li
                        key={link.message}
                        onClickCapture={() => {
                          if (link.analyticsEvent)
                            analytics.track(link.analyticsEvent);
                        }}
                      >
                        <FullscreenModalItem asChild>
                          <Link {...link.props}>{t(link.message)}</Link>
                        </FullscreenModalItem>
                      </li>
                    ))}
                    {meQuery.data?.has_access_to_admin_dashboard && (
                      <li>
                        <FullscreenModalItem asChild>
                          <Link
                            onMouseDown={() => {
                              analytics.track(
                                LINKS.parishAdminDashboard.analyticsEvent,
                              );
                            }}
                            {...LINKS.parishAdminDashboard.props}
                          >
                            {t(LINKS.parishAdminDashboard.message)}
                          </Link>
                        </FullscreenModalItem>
                      </li>
                    )}
                    {isKidsProfileVisible && (
                      <li key={"kids_profile"}>
                        <FullscreenModalItem asChild>
                          {profileSwitch}
                        </FullscreenModalItem>
                      </li>
                    )}
                    {meQuery.isSuccess &&
                      meQuery.data?.sign_in_methods?.length > 0 && (
                        <li key={"login_title"}>
                          <FullscreenModalItem asChild>
                            <LogoutButton type={"headline"} size={"l"} />
                          </FullscreenModalItem>
                        </li>
                      )}
                  </ul>
                ),
              }}
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              trigger={<li>{trigger}</li>}
            />
          </>
        )}

        {view !== "authenticated" && (
          <>
            <li css={styleDynamics.hideDesktopViewport()}>
              <IconButton
                asChild
                css={
                  isDarkOverlayVariant
                    ? undefined
                    : transparentPrimaryThemeOverride
                }
                icon={<SearchIcon.Small />}
                variant={
                  isDarkOverlayVariant ? "secondary" : "transparentPrimary"
                }
                onClickCapture={() => {
                  if (LINKS.search.analyticsEvent)
                    analytics.track(LINKS.search.analyticsEvent);
                }}
              >
                <Link {...LINKS.search.props} />
              </IconButton>
            </li>
            <li css={styleDynamics.displayDesktopViewport()}>
              <Button
                asChild
                css={[
                  transparentPrimaryThemeOverride,
                  isDarkOverlayVariant &&
                    darkOverlayDesktopTransparentPrimaryStyles,
                ]}
                startIcon={<SearchIcon.Small />}
                variant="transparentPrimary"
                onClickCapture={() => {
                  if (LINKS.search.analyticsEvent)
                    analytics.track(LINKS.search.analyticsEvent);
                }}
              >
                <Link {...LINKS.search.props}>{t(LINKS.search.message)}</Link>
              </Button>
            </li>
            <li css={styleDynamics.displayDesktopViewport()}>
              <Button
                asChild
                css={[
                  transparentPrimaryThemeOverride,
                  isDarkOverlayVariant &&
                    darkOverlayDesktopTransparentPrimaryStyles,
                ]}
                variant="transparentPrimary"
              >
                <Link
                  {...LINKS.login.props}
                  href={`/login?redirect=${encodeURIComponent(pathname)}`}
                  onClickCapture={() => {
                    if (LINKS.login.analyticsEvent)
                      analytics.track(LINKS.login.analyticsEvent);
                  }}
                >
                  {t(LINKS.login.message)}
                </Link>
              </Button>
            </li>
            <li css={styleDynamics.displayDesktopViewport()}>
              <TryHallowForFree
                buttonCss={
                  isDarkOverlayVariant ? darkOverlayDesktopCtaStyles : null
                }
              />
            </li>
            {!isDesktopViewport && (
              <FullscreenModal
                open={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                trigger={
                  <li css={styleDynamics.hideDesktopViewport()}>{trigger}</li>
                }
              >
                <ul css={ulVerticalStyles}>
                  {menu.map((link) => (
                    <li key={link.id}>
                      <FullscreenModalItem asChild>
                        <a href={link.url} target={link.target}>
                          {link.title}
                        </a>
                      </FullscreenModalItem>
                    </li>
                  ))}
                  <li key={"anon-settings"}>
                    <FullscreenModalItem asChild>
                      <Link {...LINKS.anonSettings.props}>
                        {t(LINKS.anonSettings.message)}
                      </Link>
                    </FullscreenModalItem>
                  </li>
                </ul>
                <FullscreenModalFooter css={modalFullscreenFooterStyles}>
                  <div css={modalFullscreenFooterDivVerticalStyles}>
                    <TryHallowForFree />
                    <Button
                      asChild
                      css={transparentPrimaryThemeOverride}
                      isFullWidth
                      variant="transparentPrimary"
                      size="l"
                    >
                      <Link
                        {...LINKS.login.props}
                        href={`/login?redirect=${encodeURIComponent(pathname)}`}
                        onClickCapture={() => {
                          if (LINKS.login.analyticsEvent)
                            analytics.track(LINKS.login.analyticsEvent);
                        }}
                      >
                        {t(LINKS.login.message)}
                      </Link>
                    </Button>
                  </div>
                  <div css={modalFullscreenFooterDivDynamicStyles}>
                    <DownloadAppButton
                      isFullWidth
                      store="apple"
                      onClickCapture={() => {
                        analytics.track({
                          event:
                            HallowAnalyticsEvent.TappedDownloadOnTheAppStore,
                          properties: {
                            location: "menu_bar",
                          },
                        });
                      }}
                    />
                    <DownloadAppButton
                      isFullWidth
                      store="google"
                      onClickCapture={() => {
                        analytics.track({
                          event: HallowAnalyticsEvent.TappedGetItOnGooglePlay,
                          properties: {
                            location: "menu_bar",
                          },
                        });
                      }}
                    />
                  </div>
                </FullscreenModalFooter>
              </FullscreenModal>
            )}
          </>
        )}
      </ul>
    </nav>
  );
};

MenuBar.displayName = "MenuBar";
